import { FileBox, Maximize2, Minimize2, Plus, X } from 'lucide-react';
import type { KeyboardEvent, RefObject } from 'react';
import { TabButton, type TabDragProps } from '@/components/desktop-side-panel/tab-button';
import { usePanelItems } from '@/components/desktop-side-panel/use-panel-items';
import { isAssistantView } from '@/features/assistants/assistant-views';
import type { PanelInstance } from '@/lib/side-panel/panel-instance';

const NO_SHOWN_IDS: ReadonlySet<string> = new Set();

/** The arrows, Home and End move between the tabs (and show the tab reached), as a tablist does. */
function moveBetweenTabs(
  event: KeyboardEvent<HTMLDivElement>,
  panels: readonly PanelInstance[],
  onSelectTab: (panel: PanelInstance) => void,
): void {
  const tabs = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-panel-tab]')];
  const current = tabs.indexOf(event.target as HTMLElement);
  if (current === -1 || tabs.length === 0) return;
  const next =
    event.key === 'ArrowRight'
      ? (current + 1) % tabs.length
      : event.key === 'ArrowLeft'
        ? (current - 1 + tabs.length) % tabs.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : null;
  if (next === null) return;
  event.preventDefault();
  tabs[next]?.focus();
  const panel = panels[next];
  if (panel !== undefined) onSelectTab(panel);
}

const CONTROL =
  'flex size-8 shrink-0 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40';

/**
 * The side-panel header, one tab bar for every tool and app (D34): a horizontally scrolling strip
 * of its tabs as pills, "+" (the New tab launcher, D9), maximize and close. Every interaction is
 * delegated to the owning `SidePanel` through callbacks so this stays a pure view.
 */
export function PanelTabBar({
  panels,
  activeId,
  shownIds = NO_SHOWN_IDS,
  tabScrollRef,
  tabDragProps,
  maximized,
  reviewCount,
  interactionLocked,
  interactionLockMessage,
  onSelectTab,
  onCloseTab,
  onNewTab,
  onToggleMaximize,
  onClose,
}: {
  /** The open tabs, in strip order. */
  panels: readonly PanelInstance[];
  activeId: string | null;
  /** The tabs shown with the active one, in the panel's split. */
  shownIds?: ReadonlySet<string>;
  tabScrollRef: RefObject<HTMLDivElement | null>;
  tabDragProps: (id: string) => TabDragProps;
  maximized: boolean;
  /** The files Review shows, beside its tab's name. */
  reviewCount?: number | undefined;
  interactionLocked: boolean;
  interactionLockMessage?: string;
  onSelectTab: (panel: PanelInstance) => void;
  onCloseTab: (panel: PanelInstance) => void;
  onNewTab: () => void;
  onToggleMaximize: () => void;
  onClose: () => void;
}) {
  const items = usePanelItems();
  return (
    // At the top of the docked panel, the window's drag region, clear of the Windows and Linux
    // window controls; across the window (maximized) it starts after the traffic lights.
    <header
      className={`flex h-14 shrink-0 items-center gap-1 pr-[max(0.75rem,var(--window-controls-inset,0px))] ${
        maximized ? 'pl-[max(0.75rem,var(--shell-leading-inset,0px))]' : 'pl-3'
      }`}
      data-electron-drag-region
    >
      <div
        aria-label="Panel tabs"
        aria-orientation="horizontal"
        className="flex min-w-0 items-center gap-1 overflow-x-auto"
        data-electron-no-drag-region
        data-panel-tab-scroll
        onKeyDown={(event) => moveBetweenTabs(event, panels, onSelectTab)}
        ref={tabScrollRef}
        role="tablist"
      >
        {panels.map((panel, index) => (
          <TabButton
            key={panel.id}
            active={panel.id === activeId}
            focusable={
              panel.id === activeId ||
              (index === 0 && !panels.some((candidate) => candidate.id === activeId))
            }
            shown={shownIds.has(panel.id)}
            dragProps={tabDragProps(panel.id)}
            icon={
              panel.type.startsWith('artifact:')
                ? FileBox
                : (items.find((item) => item.id === panel.type)?.icon ?? FileBox)
            }
            iconUrl={panel.iconUrl}
            iconSrc={
              panel.type.startsWith('artifact:')
                ? undefined
                : items.find((item) => item.id === panel.type)?.iconSrc
            }
            name={panel.title}
            badge={panel.type === 'review' ? reviewCount : undefined}
            onClose={isAssistantView(panel.type) ? undefined : () => onCloseTab(panel)}
            onSelect={() => onSelectTab(panel)}
          />
        ))}
      </div>
      {/* Not beside the New tab launcher itself: it already is the new tab. */}
      {activeId !== null && panels.find((panel) => panel.id === activeId)?.type !== 'new-tab' && (
        <button
          aria-label="Open a new tab"
          className={CONTROL}
          data-electron-no-drag-region
          onClick={onNewTab}
          title="New tab"
          type="button"
        >
          <Plus className="size-4" />
        </button>
      )}
      <span className="min-w-0 flex-1" />
      <button
        aria-label={maximized ? 'Restore panel size' : 'Maximize panel'}
        className={CONTROL}
        data-electron-no-drag-region
        disabled={interactionLocked}
        onClick={onToggleMaximize}
        title={
          interactionLocked
            ? interactionLockMessage
            : maximized
              ? 'Restore panel size'
              : 'Maximize panel'
        }
        type="button"
      >
        {maximized ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
      </button>
      <button
        aria-label="Close right panel"
        className={CONTROL}
        data-electron-no-drag-region
        disabled={interactionLocked}
        onClick={onClose}
        title={interactionLocked ? interactionLockMessage : 'Close panel'}
        type="button"
      >
        <X className="size-4" />
      </button>
    </header>
  );
}
